import api, { handleApiError, cachedGet } from './api';
import cacheService from './cacheService';
import networkService from './networkService';

// Alle Einkaufslisten abrufen (mit Caching)
export const getAllShoppingLists = async (options = {}) => {
  try {
    const result = await cachedGet(
      '/shopping-lists',
      'cache_shopping_lists',
      options
    );
    return { 
      success: true, 
      data: result.data,
      fromCache: result.fromCache 
    };
  } catch (error) {
    // Wenn offline und kein Cache: Versuche AsyncStorage direkt
    if (error.message === 'No cached data available and offline') {
      const cached = await cacheService.getCachedShoppingLists();
      if (cached) {
        return { 
          success: true, 
          data: cached, 
          fromCache: true,
          offline: true 
        };
      }
    }
    return { success: false, error: handleApiError(error) };
  }
};

// Einkaufsliste für einen Laden abrufen
export const getShoppingListByStore = async (storeId) => {
  try {
    const response = await api.get(`/shopping-lists/store/${storeId}`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: handleApiError(error) };
  }
};

// Artikel zur Liste hinzufügen
export const addItemToList = async (storeId, itemData) => {
  try {
    const response = await api.post(`/shopping-lists/store/${storeId}/items`, itemData);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: handleApiError(error) };
  }
};

// Item bearbeiten
export const updateItem = async (itemId, itemData) => {
  try {
    const response = await api.put(`/shopping-lists/items/${itemId}`, itemData);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: handleApiError(error) };
  }
};

// Item kaufen/abhaken (mit Offline-Support)
export const purchaseItem = async (itemId, isPurchased = true) => {
  try {
    const response = await api.post(`/shopping-lists/items/${itemId}/purchase`, { 
      is_purchased: isPurchased 
    });
    return { success: true, data: response.data };
  } catch (error) {
    // Bei Offline: In Sync-Queue und Offline-Changes speichern
    if (error.isOffline || !networkService.getIsOnline()) {
      await cacheService.addToSyncQueue({
        type: 'PURCHASE_ITEM',
        itemId,
        isPurchased,
        endpoint: `/shopping-lists/items/${itemId}/purchase`,
        method: 'POST',
        data: { is_purchased: isPurchased }
      });

      // Offline-Änderung für UI-Tracking speichern
      await cacheService.addOfflineChange({
        type: 'PURCHASE_ITEM',
        itemId,
        isPurchased,
        timestamp: Date.now()
      });
      
      return { 
        success: true, 
        data: { id: itemId, is_purchased: isPurchased },
        offline: true,
        queued: true
      };
    }
    return { success: false, error: handleApiError(error) };
  }
};

// Item von Liste entfernen
export const deleteItem = async (itemId) => {
  try {
    const response = await api.delete(`/shopping-lists/items/${itemId}`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: handleApiError(error) };
  }
};

// Alle gekauften Items eines Ladens entfernen
export const clearPurchasedItems = async (storeId) => {
  try {
    const response = await api.delete(`/shopping-lists/store/${storeId}/purchased`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: handleApiError(error) };
  }
};

// Items neu sortieren
export const reorderItems = async (storeId, items) => {
  try {
    const response = await api.put(`/shopping-lists/store/${storeId}/reorder`, { items });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: handleApiError(error) };
  }
};

// Historische Einkaufstage abrufen
export const getHistoricalPurchaseDates = async () => {
  try {
    const response = await api.get('/shopping-lists/history/dates');
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: handleApiError(error) };
  }
};

// Artikel eines historischen Einkaufstags abrufen
export const getArticlesByHistoricalDate = async (date) => {
  try {
    const response = await api.get(`/shopping-lists/history/date/${date}`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: handleApiError(error) };
  }
};

// Item von einem Laden zu einem anderen verschieben
export const moveItemToStore = async (itemId, newStoreId, itemData) => {
  try {
    const response = await api.post(`/shopping-lists/items/${itemId}/move`, {
      new_store_id: newStoreId,
      ...itemData
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: handleApiError(error) };
  }
};

